import React, { useEffect, useMemo, useState } from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { DOCUMENT } from '../constants/documentTheme';

const isPendingMessage = (msg, viewer = 'partner') => {
  if (viewer === 'traveler') {
    return msg?.sender_type === 'partner' && !Number(msg?.is_read);
  }
  return msg?.sender_type === 'user' && !Number(msg?.is_read);
};

const messageSnippet = (text, max = 96) => {
  const normalized = String(text || '').replace(/\s+/g, ' ').trim();
  if (!normalized) return '';
  if (normalized.length <= max) return normalized;
  return `${normalized.slice(0, max - 1)}…`;
};

const formatMessageTime = (dateString) => {
  if (!dateString) return '';
  try {
    return formatDistanceToNow(parseISO(String(dateString).replace(' ', 'T')), { addSuffix: false });
  } catch {
    return '';
  }
};

const initialExpandedIds = (messages, viewer = 'partner') => {
  if (!messages?.length) return [];
  const pendingIds = messages.filter((msg) => isPendingMessage(msg, viewer)).map((msg) => msg.id);
  if (pendingIds.length) return pendingIds;
  return [messages[messages.length - 1].id];
};

/**
 * Email-style expandable/collapsible inquiry message cards.
 * Unread messages from the other party open by default; otherwise the latest message opens.
 */
const PartnerInquiryMessageThread = ({
  messages = [],
  customerName,
  partnerName,
  viewer = 'partner',
}) => {
  const { t } = useTranslation();
  const [expandedIds, setExpandedIds] = useState(() => initialExpandedIds(messages, viewer));

  const messageKey = useMemo(
    () => `${viewer}|${messages.map((msg) => `${msg.id}:${msg.is_read ? 1 : 0}`).join('|')}`,
    [messages, viewer]
  );

  useEffect(() => {
    setExpandedIds(initialExpandedIds(messages, viewer));
  }, [messageKey]); // eslint-disable-line react-hooks/exhaustive-deps -- reset when loaded set changes

  const pendingCount = useMemo(
    () => messages.filter((msg) => isPendingMessage(msg, viewer)).length,
    [messages, viewer]
  );

  const toggleMessage = (id) => {
    setExpandedIds((current) => (
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id]
    ));
  };

  if (!messages.length) {
    return (
      <Typography sx={{ fontSize: 14, color: DOCUMENT.ink3 }}>
        {t('partnerLists.noAdditionalTravelerNote')}
      </Typography>
    );
  }

  return (
    <Box data-testid="partner-inquiry-message-thread">
      {pendingCount > 0 ? (
        <Typography
          data-testid="partner-inquiry-thread-pending"
          sx={{
            fontSize: 10,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: DOCUMENT.ink3,
            mb: 1.25,
          }}
        >
          {t('partnerLists.threadPending', { count: pendingCount })}
        </Typography>
      ) : null}

      <Box sx={{ display: 'grid', gap: 1.25 }}>
        {messages.map((msg) => {
          const fromPartner = msg.sender_type === 'partner';
          const pending = isPendingMessage(msg, viewer);
          const expanded = expandedIds.includes(msg.id);
          const fromLabel = viewer === 'traveler'
            ? (fromPartner
              ? (partnerName || msg.sender_name || t('myInquiries.agentFallback'))
              : t('partnerLists.youReplied'))
            : (fromPartner
              ? t('partnerLists.youReplied')
              : (customerName || t('partnerLists.customer')));
          const timeLabel = formatMessageTime(msg.created_at);
          const bodyId = `inquiry-message-body-${msg.id}`;

          return (
            <Box
              key={msg.id}
              data-testid="partner-inquiry-message"
              data-expanded={expanded ? 'true' : 'false'}
              data-pending={pending ? 'true' : 'false'}
              sx={{
                border: `1px solid ${DOCUMENT.ruleHard}`,
                backgroundColor: DOCUMENT.surface || DOCUMENT.canvas,
                overflow: 'hidden',
              }}
            >
              <Box
                component="button"
                type="button"
                data-testid="partner-inquiry-message-toggle"
                aria-expanded={expanded}
                aria-controls={bodyId}
                aria-label={expanded ? t('partnerLists.collapseMessage') : t('partnerLists.expandMessage')}
                onClick={() => toggleMessage(msg.id)}
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  textAlign: 'left',
                  border: 0,
                  background: 'transparent',
                  cursor: 'pointer',
                  font: 'inherit',
                  color: DOCUMENT.ink,
                  px: 1.75,
                  py: 1.35,
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: DOCUMENT.ink }}>
                      {fromLabel}
                      {timeLabel ? (
                        <Box component="span" sx={{ fontWeight: 400, color: DOCUMENT.ink3 }}>
                          {' · '}
                          {timeLabel}
                        </Box>
                      ) : null}
                    </Typography>
                    {pending ? (
                      <Box
                        component="span"
                        data-testid="partner-inquiry-message-pending"
                        sx={{
                          fontSize: 9.5,
                          letterSpacing: '0.12em',
                          textTransform: 'uppercase',
                          color: DOCUMENT.accent,
                          border: `1px solid ${DOCUMENT.accent}`,
                          px: 0.6,
                          py: '1px',
                          lineHeight: 1.4,
                        }}
                      >
                        {t('partnerLists.messagePending')}
                      </Box>
                    ) : null}
                  </Box>
                  {!expanded ? (
                    <Typography
                      sx={{
                        mt: 0.35,
                        fontSize: 13,
                        color: DOCUMENT.ink2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {messageSnippet(msg.message)}
                    </Typography>
                  ) : null}
                </Box>
                {expanded
                  ? <ExpandLess sx={{ fontSize: 20, color: DOCUMENT.ink3, flexShrink: 0 }} />
                  : <ExpandMore sx={{ fontSize: 20, color: DOCUMENT.ink3, flexShrink: 0 }} />}
              </Box>

              <Collapse in={expanded} timeout="auto" unmountOnExit={false}>
                <Box
                  id={bodyId}
                  sx={{
                    px: 1.75,
                    pb: 1.75,
                    pt: 0,
                    borderTop: expanded ? `1px solid ${DOCUMENT.rule}` : 0,
                  }}
                >
                  <Typography
                    sx={{
                      pt: 1.5,
                      fontSize: 14.5,
                      lineHeight: 1.7,
                      color: DOCUMENT.ink,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {msg.message}
                  </Typography>
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default PartnerInquiryMessageThread;
export { isPendingMessage, messageSnippet, initialExpandedIds };
